import type { ReactNode, ElementType } from 'react'
import React, { forwardRef, useContext } from 'react'
import { css, cx } from '@linaria/core'
import { boolAttr } from '@fs/zion-frontend-friends'
import type { PolymorphicComponentPropsWithRef, PolymorphicRef } from '@fs/zion-frontend-friends'
import { useTheme } from '../theme'
import { WhiteThemeContext } from '../theme/white-theme'
import Avatar from '../avatar'
import type { AvatarProps } from '../avatar/Avatar'
import { textAcss } from '../typography/typographyStyles'

export const baseChipCss = css`
  vertical-align: baseline;
  align-items: center;
  justify-content: center;

  max-width: 100%;
  width: fit-content;

  border: none;
  border-radius: 50px;
  transition: background-color 0.15s;
`

/* These styles, although not attached to BaseChip, are necessary for all chips.
 * The reason why we can't attach this to base chip is because some chips (like ChoiceChip and FilterChip) need to have this CSS attached at a different part of the DOM, because they are form fields */
export const chipCss = css`
  line-height: 15px;
  position: relative;

  inset-inline-start: -2px; /* Visual adjustment for horizontal alignment with other elements */

  &[data-centered] {
    inset-inline-start: 0; /* this is a dumb idea when centered */
    margin: 0 auto !important;
  }

  &[data-inline] {
    display: inline-block;
  }
`

const sideCss = css`
  --icon-inline-text: -0.38;
  display: inline-block;
  vertical-align: -7px;
  margin-top: -4px;

  &[data-position='start'] {
    margin-inline-start: -7px;
    margin-inline-end: 5px;
  }

  &[data-position='end'] {
    margin-inline-start: 5px;
    margin-inline-end: -7px;
  }
`

type ChipProps = {
  children?: ReactNode
  className?: string
  disabled?: boolean
  tabIndex?: number
  inline?: boolean
  avatarProps?: AvatarProps
  Icon?: ElementType
  iconAtEnd?: boolean
}

type ChipSideProps = {
  Icon?: ElementType
  avatarProps?: AvatarProps
  position: 'start' | 'end'
}

export const ChipSide = ({ avatarProps, Icon, position }: ChipSideProps): JSX.Element => {
  if (avatarProps) {
    return (
      <div className={sideCss} data-position="start">
        <Avatar {...avatarProps} size="xs" />
      </div>
    )
  }
  if (Icon) {
    return (
      <div className={sideCss} data-position={position}>
        <Icon aria-hidden size="sm" />
      </div>
    )
  }
  // @ts-expect-error -- TODO fix strict issue: This was grandfathered from before we adopted strict mode in typescript.
  return null
}

type BaseChipProps<C extends React.ElementType> = PolymorphicComponentPropsWithRef<C, ChipProps>
type BaseChipComponent = <C extends React.ElementType = 'span'>(props: BaseChipProps<C>) => React.ReactElement | null
const BaseChip: BaseChipComponent = forwardRef(
  <C extends React.ElementType = 'span'>(
    { as, children, className, disabled, tabIndex, Icon, avatarProps, inline, iconAtEnd, ...props }: BaseChipProps<C>,
    ref: PolymorphicRef<C>
  ) => {
    const TagName = as || 'div'
    const isWhite = useContext(WhiteThemeContext)
    const isNight = useTheme() === 'night'

    return (
      <TagName
        className={cx(className, baseChipCss, textAcss)}
        data-white={boolAttr(isWhite)}
        data-night={boolAttr(isNight)}
        aria-disabled={disabled}
        disabled={disabled}
        tabIndex={tabIndex}
        {...props}
        ref={ref}
      >
        {!inline && !iconAtEnd && <ChipSide avatarProps={avatarProps} Icon={Icon} position="start" />}
        {children}
        {!inline && iconAtEnd && <ChipSide Icon={Icon} position="end" />}
      </TagName>
    )
  }
)

export default BaseChip
